/*
 * @Description: Frequently Asked Questions section for AI Video Editor
 */
import React, { useState } from 'react';

interface FAQItem {
  id: string;
  question: string;
  answer: string | React.ReactNode;
}

interface FAQItemProps {
  item: FAQItem;
  isExpanded: boolean;
  onClick: () => void;
}

const FAQItemComponent: React.FC<FAQItemProps> = ({ item, isExpanded, onClick }) => {
  return (
    <div
      className={`
        border border-[#CDCDCD] transition-all duration-300 cursor-pointer
        ${isExpanded ? 'bg-white' : 'bg-white hover:bg-gray-50'}
      `}
      onClick={onClick}
    >
      <div className="px-6 py-6 pad:px-8 pad:py-8">
        <div className="flex items-center justify-between gap-4">
          <h3 className="flex-1 text-base pad:text-base font-semibold text-[#0F1117]">
            {item.question}
          </h3>

          <div className="flex-shrink-0 w-6 h-6 flex items-center justify-center">
            {isExpanded ? (
              <svg
                className="w-5 h-5 text-[#0F1117]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M20 12H4"
                />
              </svg>
            ) : (
              <svg
                className="w-5 h-5 text-[#0F1117]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v16m8-8H4"
                />
              </svg>
            )}
          </div>
        </div>

        <div
          className={`
            overflow-hidden transition-all duration-300
            ${isExpanded ? 'max-h-[800px] opacity-100 mt-4' : 'max-h-0 opacity-0'}
          `}
        >
          <div className="text-sm pad:text-base text-gray-600 leading-relaxed [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:mt-2 [&_ol]:space-y-1 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mt-2 [&_ul]:space-y-1">
            {item.answer}
          </div>
        </div>
      </div>
    </div>
  );
};

const FAQSection: React.FC = () => {
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set(['faq-1']));

  const faqItems: FAQItem[] = [
    {
      id: 'faq-1',
      question: 'What is an AI video editor?',
      answer:
        'An AI video editor is a tool that uses artificial intelligence to help you edit videos faster and with less manual work. Instead of dragging clips or adjusting settings by hand, you can use text prompts to cut scenes, enhance visuals, add effects, or remove video backgrounds. It makes video editing easier for beginners and faster for creators.',
    },
    {
      id: 'faq-2',
      question: 'How does an AI video editor work?',
      answer:
        'An AI video editor analyzes your video and applies edits based on your instructions. You can type what you want\u2014like \u201Cremove this part,\u201D \u201Cbrighten the scene,\u201D or \u201Cchange the background\u201D\u2014and the AI handles the technical steps. It automates tasks such as trimming, color correction, object removal, and subtitle generation, so you can focus on the content instead of the tools.',
    },
    {
      id: 'faq-3',
      question: 'What are the applications of AI video editors?',
      answer:
        'AI video editors are used for social media videos, ads, YouTube content, product demos, tutorials, interviews, and more. They help creators repurpose long videos into short clips, identify and remove \u201Cums,\u201D \u201Cuhs,\u201D and long pauses, clean up footage, and speed up editing workflows. Businesses use them to produce consistent, high\u2011quality videos without hiring a full editing team.',
    },
    {
      id: 'faq-4',
      question: 'How do I edit a video with FalcoCut?',
      answer: (
        <>
          <ol>
            <li>
              <strong>Step 1:</strong> Upload your video (MP4 or MOV) to FalcoCut.
            </li>
            <li>
              <strong>Step 2:</strong> Enter a text prompt describing the edit you
              want&mdash;cut, enhance, remove objects, adjust pacing, etc.
            </li>
            <li>
              <strong>Step 3:</strong> Upload a photo if you want the AI to reference a
              style or visual element.
            </li>
            <li>
              <strong>Step 4:</strong> Click Start, preview the result, and export your
              edited video.
            </li>
          </ol>
        </>
      ),
    },
    {
      id: 'faq-5',
      question: 'Does FalcoCut work in a browser or do I need to install software?',
      answer:
        'FalcoCut runs entirely in your browser, so there\u2019s nothing to install or update. You can access the AI video editor from any device with an internet connection, whether you\u2019re on a laptop, desktop, or mobile browser. This makes it easy to edit videos on the go and ensures you always have the latest features without managing downloads or system requirements. Everything happens online, and your projects stay synced across devices.',
    },
    {
      id: 'faq-6',
      question: 'What video formats does FalcoCut support?',
      answer:
        'FalcoCut supports MP4 and MOV files, which are widely used across phones, cameras, and editing tools. You can upload clips between 3\u201310 seconds and up to 200MB in size, giving you enough room to work with short social videos, product demos, or quick creative tests. These limits help the AI process your footage quickly and consistently, so you don\u2019t need to convert formats or worry about compatibility. Everything is designed to keep your workflow simple and efficient from the moment you upload.',
    },
    {
      id: 'faq-7',
      question: 'Is there a file size limit for video uploads?',
      answer:
        'Yes. FalcoCut allows uploads up to 200MB and 3\u201310 seconds per video. This ensures fast, stable processing and keeps the editing experience smooth for all users. If your footage is longer, you can trim it before uploading or split it into smaller segments to stay within the limits. These guidelines help maintain reliable performance while still giving you the flexibility to work with clear, high\u2011quality clips for your projects.',
    },
    {
      id: 'faq-8',
      question: 'Is FalcoCut a free AI video editor?',
      answer:
        'FalcoCut offers a free plan that gives you access to core AI editing features, including text\u2011based editing, enhancements, and basic exports. This makes it easy to try the tool without any upfront cost. Some advanced features\u2014such as higher\u2011resolution exports, watermark removal, or batch translation\u2014may require a paid plan. The free version is ideal for testing workflows, creating simple edits, and exploring what AI video editing can do for your content.',
    },
    {
      id: 'faq-9',
      question: 'Does the free plan include a watermark on the video?',
      answer:
        'Yes. Videos exported on the free plan will include a watermark. This helps keep the tool accessible while supporting more advanced features for paid users. If you need clean, watermark\u2011free exports for professional use\u2014such as ads, client work, or branded content\u2014you can upgrade to a paid plan at any time. The editing experience stays the same; only the export settings change.',
    },
    {
      id: 'faq-10',
      question: 'Does FalcoCut work on low bandwidth?',
      answer:
        'FalcoCut is designed to work smoothly even on lower\u2011bandwidth connections, though upload and export times may vary depending on your network speed. Because the editing happens in the cloud, your device doesn\u2019t need high processing power. You can still perform most tasks\u2014like entering prompts, previewing edits, and managing projects\u2014without a fast connection. A stronger network will speed up uploads and final exports, but the core editing experience remains accessible.',
    },
    {
      id: 'faq-11',
      question: 'Why is my video export stuck?',
      answer:
        'Video exports may get stuck for several reasons, including slow internet, large file size, heavy AI processing, or temporary server load. If your export seems frozen, try refreshing the page, checking your connection, or exporting again. Reducing the video length or simplifying the edit can also help. In most cases, the issue resolves quickly, and your video will finish processing once the temporary bottleneck clears. If the problem continues, re\u2011uploading the file often fixes it.',
    },
    {
      id: 'faq-12',
      question: 'Is my video data secure with FalcoCut?',
      answer:
        'Yes. FalcoCut stores your videos securely and protects your data throughout the editing process. Your files are not shared with third parties, and you remain in control of your content at all times. You can delete your videos from your account whenever you choose, and they will be removed from the system.',
    },
  ];

  const handleItemClick = (id: string) => {
    setExpandedIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  return (
    <section className="w-full bg-white py-12 pad:py-16 sm:py-20 px-4 pad:px-8 sm:px-[65px]">
      <div className="max-w-[1190px] mx-auto">
        <h2 className="text-2xl pad:text-3xl sm:text-4xl font-bold text-[#0F1117] text-center mb-10 pad:mb-12 sm:mb-16">
          Frequently Asked Questions
        </h2>

        <div className="max-w-[840px] mx-auto flex flex-col gap-2">
          {faqItems.map((item) => (
            <FAQItemComponent
              key={item.id}
              item={item}
              isExpanded={expandedIds.has(item.id)}
              onClick={() => handleItemClick(item.id)}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default FAQSection;
